import type { GetFactoringExtensionPendingAccountDetail } from 'lib/api/extension/factoring/types';

import type {
  ExtensionReportData,
  ExtensionReportDocumentDetail,
} from '../../exportExtensionReport/types';
import type { ExtensionCheckboxesState } from '../hooks/useExtensionCheckboxes';
import type { ExtensionExportRequest } from '../types/ExtensionExportRequest';

export interface BuildExtensionRequestOptions {
  extension: ExtensionReportData | Record<string, unknown>;
  checkboxes: ExtensionCheckboxesState;
  /** Datos del documento (perfil de deuda) para montos en el PDF */
  document?: Record<string, unknown> | null;
  pendingAccounts?: GetFactoringExtensionPendingAccountDetail[];
}

function toNumber(value: unknown): number {
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value === 'string') return Number(value) || 0;
  return 0;
}

function toStringOrUndefined(value: unknown): string | undefined {
  if (value == null) return undefined;
  return String(value);
}

function mapDocumentDetail(
  document: Record<string, unknown> | null | undefined,
): ExtensionReportDocumentDetail | undefined {
  if (document == null) return undefined;
  const hasAny =
    document.startingPaymentDate != null ||
    document.paymentDate != null ||
    document.amount != null ||
    document.amountConciliated != null ||
    document.totalRemaining != null;
  if (!hasAny) return undefined;

  return {
    startingPaymentDate: document.startingPaymentDate as
      | Date
      | string
      | null
      | undefined,
    paymentDate: document.paymentDate as Date | string | null | undefined,
    amount:
      document.amount != null && document.amount !== ''
        ? toNumber(document.amount)
        : null,
    amountConciliated:
      document.amountConciliated != null && document.amountConciliated !== ''
        ? toNumber(document.amountConciliated)
        : null,
    totalRemaining:
      document.totalRemaining != null && document.totalRemaining !== ''
        ? toNumber(document.totalRemaining)
        : null,
  };
}

export function buildExtensionRequest(
  options: BuildExtensionRequestOptions,
): ExtensionExportRequest {
  const { extension, checkboxes, document, pendingAccounts } = options;
  const ext = extension as Record<string, unknown>;

  const data: ExtensionReportData = {
    id: toNumber(ext.id),
    status: toStringOrUndefined(ext.status),
    statusEsp: toStringOrUndefined(ext.statusEsp),
    rate: toNumber(ext.rate),
    rateCost: toNumber(ext.rateCost),
    operationCostFixed: toNumber(ext.operationCostFixed),
    operationCostVariable: toNumber(ext.operationCostVariable),
    operationCostRateVariable:
      ext.operationCostRateVariable != null
        ? toNumber(ext.operationCostRateVariable)
        : null,
    startDate:
      ext.startDate instanceof Date
        ? ext.startDate
        : String(ext.startDate ?? ''),
    dueDate:
      ext.dueDate instanceof Date ? ext.dueDate : String(ext.dueDate ?? ''),
    createdAt:
      ext.createdAt instanceof Date
        ? ext.createdAt
        : String(ext.createdAt ?? ''),
    observation: ext.observation != null ? String(ext.observation) : null,
    documentId: toStringOrUndefined(ext.documentId),
    folio: toStringOrUndefined(ext.folio),
    businessName: toStringOrUndefined(ext.businessName),
    businessIdentifier: toStringOrUndefined(ext.businessIdentifier),
    stakeholderName: toStringOrUndefined(ext.stakeholderName),
    stakeholderIdentifier: toStringOrUndefined(ext.stakeholderIdentifier),
    documentDetail: mapDocumentDetail(document ?? null),
    pendingAccounts:
      pendingAccounts != null && pendingAccounts.length > 0
        ? pendingAccounts
        : undefined,
  };

  return {
    ...data,
    fieldVisibility: {
      showDocumentInfo: checkboxes.showDocumentInfo,
      showExtensionInfo: checkboxes.showExtensionInfo,
      showDates: checkboxes.showDates,
      showParamsAndCosts: checkboxes.showParamsAndCosts,
      showObservation: checkboxes.showObservation,
      showDocumentFinancials: checkboxes.showDocumentFinancials,
      showPendingAccounts: checkboxes.showPendingAccounts,
    },
  };
}
